import React, { FunctionComponent, useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import IPost from '@interfaces/IPost';
import cn from 'classnames';
import {
  CalendarOutlined,
  CommentOutlined,
  EyeOutlined,
  HeartOutlined,
  LinkOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import { FULL_TIME_FORMAT } from '@config/format';
import moment from 'moment';
import { setList as setGameList } from '@stores/gamesSelector';
import TextWithHashtags from '@components/TextWithHashtags';
import { Button, Input, Space, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { formatPostUrl, getPath, goTo } from '@helpers/navigation';
import { setPost } from '@stores/post';
import UserShortCard from '@components/UserShortCard';
import { get } from 'lodash';
import useTranslate from '@hooks/useTranslate';
import componentContent from './MentionsOfficialAccountPostCard.module.json';
import ViewFiles from '@components/ViewFiles/ViewFiles';
import useModal from '@hooks/useModal';
import ImageModal from '@components/FullImageModal/FullImageModal';
import ShowMore from '@fields/ShowMore/ShowMore';
import auth from '@stores/auth';

import { checkPost, replyToPost } from '@stores/mentionsOfficialAccountPost';
import { block as blockPost, hidePost } from '@stores/posts';

import css from './MentionsOfficialAccountPostCard.module.less';

const { TextArea } = Input;

interface MentionsOfficialAccountPostCardProps {
  post: IPost;
}

const MentionsOfficialAccountPostCard: FunctionComponent<MentionsOfficialAccountPostCardProps> = ({
  post,
}) => {
  const content = useTranslate(componentContent);
  const { user: authUser } = useStore(auth);
  const replyAccountId = authUser?.id ? String(authUser.id) : '';

  const [isVisible, openModal, closeModal] = useModal();
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');

  const [isChecking, setIsChecking] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [isSpamming, setIsSpamming] = useState(false);
  const [isShadowbanning, setIsShadowbanning] = useState(false);

  const skipDisabled = isReplying || isChecking || isSpamming || isShadowbanning;
  const actionsDisabled = isReplyOpen || isChecking || isReplying || isSpamming || isShadowbanning;
  const sendDisabled =
    !replyText.trim() ||
    !replyAccountId ||
    isReplying ||
    isChecking ||
    isSpamming ||
    isShadowbanning;

  if (!post) return null;

  const filterByUser = () => goTo('userDetail', { userId: post.author.id });
  const filterByTag = (hashtag: string) => goTo('postList', undefined, { hashtag });
  const filterByGame = () => {
    setGameList([post.game]);
    goTo('postList', undefined, { gameId: post.game.id });
  };
  const postUrl = post.mobileUrlPost ? formatPostUrl(post.mobileUrlPost) : null;

  const onSkip = () => {
    if (skipDisabled) return;
    setIsChecking(true);
    checkPost({ postId: post.id, preloaderId: `checkPost_${post.id}` }).finally(() =>
      setIsChecking(false),
    );
  };

  const onOpenReply = () => setIsReplyOpen(true);

  const onCancelReply = () => {
    setIsReplyOpen(false);
    setReplyText('');
  };

  const onSendReply = () => {
    const text = replyText.trim();
    if (!text || !replyAccountId || isReplying || isChecking) return;

    setIsReplying(true);

    replyToPost({
      postId: String(post.id),
      text,
      replyAccountId,
      images: [],
    }).finally(() => setIsReplying(false));
  };

  const onSpam = () => {
    if (actionsDisabled) return;
    setIsSpamming(true);
    blockPost({
      postId: post.id,
      preloaderId: `block_${post.id}`,
    }).finally(() => setIsSpamming(false));
  };

  const onShadowban = () => {
    if (actionsDisabled) return;
    setIsShadowbanning(true);
    hidePost({
      postId: post.id,
      preloaderId: `hidePost_${post.id}`,
    }).finally(() => setIsShadowbanning(false));
  };

  useEffect(() => {
    const unsub = replyToPost.done.watch(({ params }) => {
      if (String(params.postId) === String(post.id)) {
        onCancelReply();
      }
    });

    return () => unsub();
  }, [post.id]);

  return (
    <div className={css.root}>
      <div className={cn(css.images, css.notSpam)}>
        <>
          <div
            className={css.overlay}
            onClick={openModal}
          >
            <div className={css.overlayItem}>
              <ZoomInOutlined />
            </div>
          </div>
          <ViewFiles files={post.files} />
        </>
      </div>

      <div className={css.content}>
        <div className={css.header}>
          <div className={css.headerUserName}>
            <UserShortCard
              user={post.author}
              onClick={filterByUser}
            />
            <div className={css.meta}>
              <span className={css.metaIcon}>
                <CalendarOutlined />
              </span>
              <span>{moment(post.createdDateTime).format(FULL_TIME_FORMAT)}</span>
            </div>
          </div>

          <div className={cn(css.metaLine, css.meta)}>
            <Link
              to={getPath('postDetailLikes', { postId: post.id })}
              className={cn(css.metaLineItem, 'clickable')}
              onClick={() => setPost(post)}
            >
              <span className={css.metaIcon}>
                <HeartOutlined />
              </span>
              <span>{post.likesCount}</span>
            </Link>

            <Link
              to={getPath('postDetail', { postId: post.id })}
              className={cn(css.metaLineItem, 'clickable')}
              onClick={() => setPost(post)}
            >
              <span className={css.metaIcon}>
                <CommentOutlined />
              </span>
              <span>{post.commentsCount}</span>
            </Link>

            <div className={css.metaLineItem}>
              <span className={css.metaIcon}>
                <EyeOutlined />
              </span>
              <span>{post.viewsCount || 0}</span>
            </div>

            <div className={css.metaLineItem}>
              <span
                className='clickable'
                onClick={filterByGame}
              >
                {get(post, ['game', 'name'], 'undefined')}
              </span>
            </div>
          </div>

          <div>
            {post.playerID ? (
              <div className={css.gameId}>
                <span>{content.nickname}: </span>
                <span>{post.playerID.contact}</span>
              </div>
            ) : null}

            {postUrl ? (
              <div className={cn(css.metaLine, css.meta)}>
                <Typography.Link
                  copyable={{ text: postUrl }}
                  href={postUrl}
                  target='_blank'
                  className={cn(css.metaLineItem, 'clickable')}
                >
                  <span className={css.metaIcon}>
                    <LinkOutlined />
                  </span>
                  <span>{postUrl}</span>
                </Typography.Link>
              </div>
            ) : null}
          </div>
        </div>

        <div className={css.body}>
          <ShowMore height={184}>
            <TextWithHashtags
              onHashtagClick={filterByTag}
              source={post.tags}
            >
              {post.text}
            </TextWithHashtags>
          </ShowMore>
        </div>

        <div className={css.footer}>
          <div className={css.footerActions}>
            <Space>
              <Button
                onClick={onOpenReply}
                disabled={isChecking || isReplying || actionsDisabled}
                loading={isReplying}
              >
                {content.reply}
              </Button>

              <Button
                onClick={onSkip}
                disabled={skipDisabled || actionsDisabled}
                loading={isChecking}
              >
                {content.skip}
              </Button>

              <Button
                onClick={onSpam}
                disabled={actionsDisabled}
                loading={isSpamming}
              >
                {content.spam}
              </Button>

              <Button
                onClick={onShadowban}
                disabled={actionsDisabled}
                loading={isShadowbanning}
              >
                {content.shadowban}
              </Button>
            </Space>
          </div>

          {isReplyOpen ? (
            <div style={{ marginTop: 12 }}>
              <TextArea
                autoSize={{ minRows: 3, maxRows: 6 }}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={content.replyPlaceholder}
              />

              <div style={{ marginTop: 12 }}>
                <Space>
                  <Button
                    onClick={onCancelReply}
                    disabled={!!isReplying}
                  >
                    {content.cancel}
                  </Button>

                  <Button
                    type='primary'
                    onClick={onSendReply}
                    disabled={sendDisabled}
                    loading={isReplying}
                  >
                    {content.send}
                  </Button>
                </Space>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {isVisible ? (
        <ImageModal
          onClose={closeModal}
          files={post.files}
        />
      ) : null}
    </div>
  );
};

export default MentionsOfficialAccountPostCard;
